import { FC, memo, useMemo } from "react";
import styles from "./HackathonCard.module.css";

import { IHackathonResponse } from "api/hackathon";

import { Col, Row, Skeleton, Space, Tooltip } from "antd";
import Person from "assets/svgs/Person";
import ClockOutline from "assets/svgs/ClockOutline";
import MapPin from "assets/svgs/MapPin";
import Calendar from "assets/svgs/Calendar";
import { ReactNode } from "react-markdown";
import { Tag } from "components/Tag";
import { useSearchHostGlobal, useSearchTagGlobal } from "hooks/useSearchGlobal";
import { useTimezone } from "hooks/useTimezone";
import { useDeadline } from "hooks/useDeadline";
import { getCurrentUTCTime, getStartEndFormat } from "utils/dateUtils";
import { getTotalPrize } from "utils/currency";
import { getImageUrl } from "utils/urlParser";
import { getHackathonDomain } from "utils/domain";
import { routesEnum } from "pages/Routes";
import useImageLoaded from "hooks/useImageLoaded";

interface Props {
  data?: IHackathonResponse;
}

const HackathonCard: FC<Props> = ({ data }) => {
  const doSearch = useSearchHostGlobal();
  const doSearchTag = useSearchTagGlobal();
  const timezone = useTimezone(data);
  const { startDate, deadline } = useDeadline(data);

  const totalPrizes = useMemo(() => {
    const total = getTotalPrize(data);
    if (!total) return `non-monetary`;
    return `${total}`;
  }, [data]);

  const currentTimezone = useMemo(() => {
    return `${getCurrentUTCTime(timezone.value).format("H:mm")}`;
  }, [timezone]);
  const [ref, loaded, onLoad] = useImageLoaded();

  const location = useMemo(() => {
    switch (data?.essentials?.types) {
      case "Public-Online/Virtual":
        return "Online Hackathon ";
      case "Public-Offline":
        return "In-Person";
      case "Internal-Online/Virtual":
        return "Invite-only Online Hackathon";
      case "Internal-Offline":
        return "Invite-only & In-Person";
      default:
        return "Online Hackathon ";
    }
  }, [data?.essentials?.types]);

  return (
    <a
      href={`${getHackathonDomain(data?.essentials?.url)}${
        routesEnum.hackathon
      }/overview/${data?._id}`}
      className="default"
    >
      <div className={styles.root}>
        <div className={styles.image}>
          {!loaded && <Skeleton.Button active className={styles.skeleton} />}
          <img
            src={
              getImageUrl(data?.design?.thumbnailImage) ||
              "/images/landing/blank.png"
            }
            alt="thumbnail"
            ref={ref}
            style={loaded ? {} : { display: "none" }}
            onLoad={onLoad}
          />
        </div>

        <div className={styles.cardBody}>
          <Space align="baseline" size={8}>
            <span className={styles.tag}>
              <Tooltip title={totalPrizes}>{totalPrizes}</Tooltip>
            </span>
            <div className="tint-2 text-14">
              in prizes - {data?.participants?.length || 0}
              &nbsp;participants
            </div>
          </Space>

          <h3 className="mt-24 mb-16 text-20 medium tint poppins">
            {data?.essentials?.name}
          </h3>

          <p className="tint-3 mb-16">
            {data?.essentials?.tagLine}
            <br />
            <span className="success-2">{data?.essentials?.mailManage}</span>
          </p>

          <Row gutter={[20, 16]}>
            <Col xs={12}>
              <IconItem icon={<Person />}>
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    doSearch(data?.essentials?.host || "");
                  }}
                >
                  {data?.essentials?.host}
                </span>
              </IconItem>
            </Col>
            <Col xs={12}>
              <IconItem icon={<ClockOutline />}>
                {currentTimezone}
                <div className={styles.timezone}>{timezone.utc}</div>
              </IconItem>
            </Col>
            <Col xs={12}>
              <IconItem icon={<MapPin />}>{location}</IconItem>
            </Col>
            <Col xs={12}>
              <IconItem icon={<Calendar />}>
                {getStartEndFormat(startDate, deadline)}
              </IconItem>
            </Col>
          </Row>

          <Space className="mt-24" align="start" size={13}>
            <span className="nowrap tint medium">Tags:</span>
            <Space size={8} wrap>
              {data?.essentials?.themeTags?.map((tag) => (
                <Tag
                  key={tag}
                  type="secondary"
                  hoverable
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    doSearchTag(tag);
                  }}
                >
                  {tag}
                </Tag>
              ))}
            </Space>
          </Space>
        </div>
      </div>
    </a>
  );
};

export default memo(HackathonCard);

const IconItem: FC<{ icon: ReactNode }> = ({ icon, children }) => (
  <Space className="nowrap" wrap={false} size={8}>
    <div className={styles.icon}>{icon}</div>
    <span className={styles.textConcat}>{children}</span>
  </Space>
);
